import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { AlertDialogComponent } from '../alert-dialog/alert-dialog.component';
import { AlertDialogData, AlertDialogResult } from '../alert-dialog/alert-dialog-config';

/**
 * Single entry point for all app notifications, replacing SweetAlert2 (Swal).
 *
 * Why a service over Swal:
 *  - MatDialog hosts in the CDK overlay → no background scroll-jump, no z-index
 *    fights with the rest of the UI (the issues we had with Swal).
 *  - One eMeni-styled component for every variant (alert / confirm / prompt).
 *
 * API mirrors the Swal patterns we used so call sites migrate 1:1:
 *   Swal.fire({icon:'error', title, text})            → alert.error(title, text)
 *   Swal.fire({..showCancelButton.., isConfirmed})    → alert.confirm({...}) → result.confirmed
 *   Swal.fire({input:'text', .. result.value})        → alert.prompt({...})  → result.value
 *   Swal.close() / Swal.isVisible()                   → ref.close() / alert.isAnyOpen
 */
@Injectable({ providedIn: 'root' })
export class AlertService {
  private openRefs = new Set<MatDialogRef<AlertDialogComponent, AlertDialogResult>>();

  constructor(private dialog: MatDialog) {}

  /** True while any alert dialog is on screen (replaces `Swal.isVisible()`). */
  get isAnyOpen(): boolean {
    return this.openRefs.size > 0;
  }

  /**
   * Low-level open — returns the MatDialogRef so callers that need to close it
   * programmatically (e.g. the offline interceptor) can keep the handle.
   */
  openRef(data: AlertDialogData): MatDialogRef<AlertDialogComponent, AlertDialogResult> {
    const ref = this.dialog.open<AlertDialogComponent, AlertDialogData, AlertDialogResult>(
      AlertDialogComponent,
      {
        data,
        panelClass: ['emeni-dialog', 'emeni-dialog--md'],
        autoFocus: data.input ? '.emeni-popup__input' : false,
        restoreFocus: false,
        disableClose: data.dismissible === false,
      }
    );
    this.openRefs.add(ref);
    ref.afterClosed().subscribe(() => this.openRefs.delete(ref));
    return ref;
  }

  /** Open and await the result (`{confirmed, value}`). */
  open(data: AlertDialogData): Promise<AlertDialogResult> {
    return firstValueFrom(this.openRef(data).afterClosed()).then(
      (r) => r ?? { confirmed: false }
    );
  }

  // ── Convenience one-liners (single OK button) ───────────────────────────
  error(title: string, text = ''): Promise<AlertDialogResult> {
    return this.open({ type: 'error', title, text });
  }
  warning(title: string, text = ''): Promise<AlertDialogResult> {
    return this.open({ type: 'warning', title, text });
  }
  info(title: string, text = ''): Promise<AlertDialogResult> {
    return this.open({ type: 'info', title, text });
  }
  success(title: string, text = ''): Promise<AlertDialogResult> {
    return this.open({ type: 'success', title, text });
  }

  /** Yes/No confirmation. `result.confirmed` === Swal `result.isConfirmed`. */
  confirm(data: AlertDialogData): Promise<AlertDialogResult> {
    return this.open({ type: 'question', showCancelButton: true, ...data });
  }

  /** Text prompt. `result.value` holds the entered text when confirmed. */
  prompt(data: AlertDialogData): Promise<AlertDialogResult> {
    return this.open({ input: true, showCancelButton: true, ...data });
  }

  /** Close every open alert (replaces `Swal.close()` when no ref is held). */
  closeAll(): void {
    this.openRefs.forEach((r) => r.close());
  }
}
